import config from '@/config'
import axios from 'axios'
import Moment from 'moment'
import alertHelper from '@/helpers/alert'
import paymentHelper from '@/helpers/payment'

const storeModule = {
  namespaced: true,
  state: {
    payments: [],
    house: [], // Payments of currently selected house
    weekly: [],
    new: [],
    infoChanged: false,
  },
  getters: {
    /**
     * Returns `selectedHouse` payments that are current (state 0/1)
     * Used only for display purposes
     *
     * @param  {object}
     * @return {array}
     */
    get: (state) => {
      // Only send the completed (1) and incompleted (0) ones
      // state 2 is for records to be deleted so no point in displaying them
      return state.house.filter(
        (payment) => +payment.state === 0 || +payment.state === 1
      )
    },
    /**
     * Return all payments of `selectedHouse`
     *
     * @param  {object}
     * @return {array}
     */
    getAll: (state) => {
      return state.house
    },
    getWeekly: (state) => {
      return state.weekly
    },
    getPayments: (state) => {
      return state.payments
    }
  },
  mutations: {
    setHousePayments(state, data) {
      state.house = data
    },
    setPayments(state, data) {
      state.payments = data
    },
    /**
     * Merges the new payments data with updated payments
     *
     * @param {object} state
     * @param {object} payments updated payments
     */
    updatePayments(state, payments) {
      const existingPayments = state.payments
      // Keep a map of ids so we can track the ids to update
      const updatedPaymentsIds = payments.map(p => p.id)

      // Loop through the existing payments and merge the new data
      existingPayments.forEach(ep => {
        if (updatedPaymentsIds.indexOf(ep.id) !== -1) {
          Object.assign(ep, payments[updatedPaymentsIds.indexOf(ep.id)])
        }
      })

      state.payment = existingPayments
    },
    setNew(state, payments) {
      state.new = payments
    },
    setInfoChanged(state, infoChanged) {
      state.infoChanged = infoChanged
    },
    // Use this to update payment when it has an id
    updateHousePayment(state, { data, id }) {
      if (state.house.length > 0) {
        state.house.forEach((housePayment, index) => {
          if (housePayment.id === id) {
            Object.assign(housePayment, data)
          }
        })
      }

      if (state.weekly.length > 0) {
        state.weekly.forEach((weeklyPayment, index) => {
          if (weeklyPayment.id === id) {
            if (+data.state === 0) {
              data.display_amount = ''
            } else {
              data.display_amount = `$${data.total_amount}`
            }
            Object.assign(weeklyPayment, data)
          }
        })
      }
    },
    // Use this to update payment when you dont have an id
    updateHousePaymentByPaymentNumber(state, { data, paymentNumber }) {
      state.house.forEach((housePayment, index) => {
        if (housePayment.payment_no === paymentNumber) {
          Object.assign(housePayment, data)
        }
      })
    },
  },
  actions: {
    async fetch(context) {
      const today = Moment()
      const url = config.apiBaseUrl + 'payment'
      try {
        const response = await axios.get(url, {
          params: {
            state: '0,1',
            paydue_time_begin: today.startOf('week').format('YYYY-MM-DD'),
            paydue_time_end: today.endOf('week').format('YYYY-MM-DD'),
          },
        })
        if (response.data.errmsg === '') {
          let weeklyPayments = response.data.data
          for (let payment of weeklyPayments) {
            payment.unit = ''
            payment.num = ''
            payment.street = ''
            payment.suburb = ''
            payment.city = ''
            payment.country = ''
            if (+payment.state === 0) {
              payment.display_amount = ''
            } else {
              payment.display_amount = `$${payment.total_amount}`
            }
          }

          context.commit('setPayments', weeklyPayments)
        } else {
          alertHelper.error(response.data.errmsg)
        }
      } catch (error) {
        alertHelper.error(error)
      }
    },
    async fetchWeeklyHouseViaPaymentsAddress(context) {
      await context.dispatch('HouseList/list', null, { root: true })
      await context.dispatch('fetch')

      let weeklyPayments = context.rootGetters['HouseList/Payments/getPayments']

      for (let payment of weeklyPayments) {
        const houseObject = findHouseAddressById(
          payment.house_id,
          context.rootGetters['HouseList/getHouseList']
        )
        if (houseObject) {
          payment.unit = houseObject.unit
          payment.num = houseObject.num
          payment.street = houseObject.street
          payment.suburb = houseObject.suburb
          payment.city = houseObject.city
          payment.country = houseObject.country
          if (payment.state == 0) {
            const unpaidFees = houseObject.fee.filter(f => f.state === 0 && !f.payment_id);
            // console.log('fetchWeeklyHouseViaPaymentsAddress, weeklyPayments.forEach: ', {state:payment.state, unpaidFees})
            payment.unpaidFeesCount = unpaidFees.length;
            payment.fee = unpaidFees   // 4-28-2021
            for(const f of unpaidFees) {
              f.payment_id = payment.id;
            }
            const curLease = house.lease.find(lease => lease.id === payment.lease_id);
            payment.balance = curLease? curLease.balance : 0;
          }
        }
      }

      weeklyPayments.map(i => {
        if (+i.state === 0) {
          i.display_amount = ''
        } else {
          i.display_amount = `$${i.total_amount}`
        }
        return i
      })
      context.commit('setPayments', weeklyPayments)
    },
    async fetchPaymentsByHouseId(context, id) {
      try {
        const response = await axios.get(
          config.apiBaseUrl + 'payment/getByHouseId',
          { params: { house_id: id } }
        )

        if (response.data.errmsg === '') {
          let existingPayments = response.data.data
          if (existingPayments.length) {
            existingPayments = sortPayments(existingPayments)

            const payments = generatePaymentPeriodArray(
              context.rootGetters['HouseList/getSelectedHouse'],
              existingPayments.length > 0
                ? existingPayments[existingPayments.length - 1].paydue_time
                : '',
              existingPayments.length > 0
                ? existingPayments[existingPayments.length - 1].payend_time
                : '',
              existingPayments.length
            )
            context.commit(
              'setHousePayments',
              existingPayments.concat(payments)
            )
          } else {
            context.dispatch('fetchNewPayments')
          }
        } else {
          alertHelper.error(response.data.errmsg)
        }
      } catch (error) {
        alertHelper.error(
          'Sorry, we are unable to complete the request. Please refresh your browser and try again:' +
            error.toString()
        )
      }
    },
    fetchNewPayments(context) {
      const house = context.rootGetters['HouseList/getSelectedHouse']
      const existingPayments = context.getters['get']

      const payDueTime =
        existingPayments.length > 0
          ? existingPayments[existingPayments.length - 1].paydue_time
          : ''
      const payEndTime =
        existingPayments.length > 0
          ? existingPayments[existingPayments.length - 1].payend_time
          : ''

      const payments = generatePaymentPeriodArray(
        house,
        payDueTime,
        payEndTime,
        existingPayments.length
      )

      context.commit('setNew', payments)
      context.commit('setInfoChanged', true)
    },
    /**
     * This function will save/update the payment based on the id
     *
     * @param {object} context
     * @param {object} payment
     */
    async save(context, payment) {
      let data = {}

      Object.assign(data, payment)
      delete data.fee // API doesnt need fee data at this point

      try {
        let response = ''
        let id = ''

        if (payment.id) {
          // We have an id so this will be an update of payment
          id = payment.id
          response = await axios.put(config.apiBaseUrl + 'payment/' + id, data)
        } else {
          // We dont have an id so save the payment
          response = await axios.post(config.apiBaseUrl + 'payment', data)
        }

        if (response.data.errno === 0) {
          if (id) {
            // Update the house by its payment id
            context.commit('updateHousePayment', { data, id })
            // Update the payment fees
            if (payment.fee.length > 0) {
              payment.fee.forEach((paymentFee) => {
                paymentFee.payment_id = payment.id
                paymentFee.state = 1
                context.dispatch('HouseList/Fees/update',
                  { fee: paymentFee },
                  { root: true }
                )
              })
            }
          } else {
            // We dont have an id until this point
            // so the only unique thing we have is the `payment_no`
            context.commit('updateHousePaymentByPaymentNumber', {
              data: {
                mf_gst_status: data.mf_gst_status,
                id: response.data.data.id,
              },
              paymentNumber: data.payment_no,
            })
          }

          alertHelper.success('Payment saved successfully')
        } else {
          alertHelper.error(response.data.errmsg)
        }
      } catch (error) {
        alertHelper.error(
          'Sorry, we are unable to complete your request. Please refresh your browser and try again.'
        )
      }
    },
    /**
     * Bulk saves payments when payment info is changed
     *
     * @param  {object}
     */
    async saveAll(context) {
      const isInfoChanged = context.getters['getInfoChanged']
      // Get all existing payments for this house
      const existingPayments = JSON.parse(
        JSON.stringify(context.getters['getAll'])
      )
      // Newly generates ones based on the new pay info
      let newPayments = JSON.parse(JSON.stringify(context.getters['getNew']))

      // We only want to update payments in bulk when the pay info is changed
      if (isInfoChanged) {
        try {
          let newPaymentsWithId = []
          let existingPaymentsToDelete = []

          if (existingPayments.length > 0) {
            // Loop to filter the existing payments
            for (let i = 0; i <= existingPayments.length - 1; i++) {
              if (+existingPayments[i].state === 2) {
                existingPaymentsToDelete.push(existingPayments[i])
                existingPayments.splice(i, 1)
                i--
              }
            }
          }

          // Update the existing payments in database to delete state
          if (existingPaymentsToDelete.length > 0) {
            await axios.put(
              config.apiBaseUrl + 'payment/putMany',
              existingPaymentsToDelete
            )
          }

          if (newPayments.length > 0) {
            // Save the new ones
            let response = await axios.post(
              config.apiBaseUrl + 'payment/postMany',
              newPayments
            )

            if (response.data.errno === 0) {
              // We get ids back for each record
              // Update the newly generated payments with these ids
              const ids = response.data.data
              newPaymentsWithId = newPayments.map((newPayment, index) => {
                newPayment['id'] = ids[index]
                return newPayment
              })

              context.commit(
                'setHousePayments',
                existingPayments.concat(newPaymentsWithId)
              )
              context.commit('setNew', [])
            } else {
              alertHelper.error(response.data.errmsg)
            }
          }
        } catch (error) {
          alertHelper.error(error)
        }
      }
    },
    async bulkUpdate(context, payments) {
      try {
        const paymentResponse = await axios.put(
          config.apiBaseUrl + 'payment/putMany',
          payments
        )

        if (paymentResponse.data.errno === 0) {
          context.commit('updatePayments', payments)
          alertHelper.success('Payments are processed successfully')
        } else {
          alertHelper.error(paymentResponse.data.errmsg)
        }
      } catch (error) {
        alertHelper.error(
          'Sorry, we are unable to complete your request. Please refresh your browser and try again.'
        )
      }
    },
    /**
     * This function will change the states (to 1) of fees (if any) and payment
     * and sends a request to generate a pdf of supplied payment
     *
     * @param {object} context
     * @param {object} data     house payment
     */
    async downloadReportPdf(context, data) {
      let payment = data

      try {
        await axios
          .post(
            config.apiBaseUrl + 'payment/downloadPDF',
            { id: payment.id },
            {
              responseType: 'blob',
            }
          )
          .then((response) => {
            paymentHelper.outputPdfAtNewTab(response, payment.id + '.pdf')
          })
      } catch (error) {
        alertHelper.error(
          'Sorry, we are unable to complete your request. Please refresh your browser and try again.'
        )
      }
    },
    async sendReport(context, payment) {
      try {
        const payment_properties = {...payment }
        delete payment_properties.fee
        delete payment_properties.tenant

        const response = await axios.post(
          config.apiBaseUrl + 'payment/sendPayment',
          { data: [payment_properties] }
        )

        if (response.data.errno === 0) {
          context.commit('updateSelectedPayment', { ...payment_properties, payment_pdf_send: 1})
          alertHelper.success('Report sent successfully to the owner')
        } else {
          alertHelper.error(response.data.errmsg)
        }
      } catch (error) {
        alertHelper.error(
          'Sorry, we are unable to complete your request. Please refresh your browser and try again.'
        )
      }
    },
    async archivePayment(context, payment) {
      // console.log('houselist.payment.archivepayment', payment_properties)
      const payment_properties = {...payment }
      delete payment_properties.fee
      delete payment_properties.tenant

      try {
        const response = await axios.post(
          config.apiBaseUrl + 'payment/archivePayment',
          payment_properties
        )

        if (response.data.errno === 0) {
          context.commit('updateSelectedPayment', { ...payment_properties, payment_pdf_send: 1})
          alertHelper.success('Payment archived successfully')
        } else {
          alertHelper.error(response.data.errmsg)
        }
      } catch (error) {
        alertHelper.error(
          'Sorry, we are unable to complete your request. Please refresh your browser and try again.'
        )
      }
    },
    /**
     * Updates the payment info changed flag
     *
     * @param  {object}
     * @param  {boolean} true | false
     */
    updateInfoChanged(context, status) {
      // When payment info is changed update the existing payments to state 2
      // so we can calculate the new ones
      if (context.state.house.length > 0) {
        context.state.house.forEach((payment) => {
          if (+payment.state === 0) {
            payment.state = 2
          }

          return payment
        })
      }

      context.commit('setInfoChanged', status)
    },
    async redo(context, id) {
      const paymentFees = context.rootGetters['HouseList/Fees/get'].filter(
        (fee) => fee.payment_id === id
      )

      try {
        // console.log({ paymentFees })
        if (paymentFees.length > 0) {
          paymentFees.forEach((paymentFee) => {
            // console.log({ paymentFee })
            paymentFee.payment_id = ''
            paymentFee.state = 0
            context.dispatch('HouseList/Fees/update',
              { fee: paymentFee },
              { root: true }
            )
          })
        }

        const paymentData = {
          state: '0',
          pay_time: null,
          fee: [],
        }
        const response = await axios.put(
          config.apiBaseUrl + 'payment/' + id,
          paymentData
        )

        if (response.data.errno === 0) {
          context.commit('updateHousePayment', { data: paymentData, id: id })
          alertHelper.success('Payment is reverted to unprocessed state')
        } else {
          alertHelper.error(response.data.errmsg)
        }

      } catch (error) {
        alertHelper.error(
          'Sorry, we are unable to complete your request. Please refresh your browser and try again.'
        )
      }
    },
    async getLastBalance(context, payment) {
      try {
        const response = await axios.get(config.apiBaseUrl + 'payment/getLastBalance', {params: { id: payment.id }})
        console.log(response.data.data);
        return response.data.data.lastBalance
      } catch (error) {
        alertHelper.error(
          'Sorry, we are unable to get last balance. Please refresh your browser and try again.'
        )
      }
    },
    async processPayments(context, payments) {
      try {
        const paymentResponse = await axios.post(
          config.apiBaseUrl + 'payment/processPayments',
          payments
        )

        if (paymentResponse.data.errno === 0) {
          payments = paymentResponse.data.data;
          context.commit('updatePayments', payments)
          alertHelper.success('Payments are processed successfully')
        } else {
          alertHelper.error(paymentResponse.data.errmsg)
        }
      } catch (error) {
        alertHelper.error(error.toString())
      }
    },

  },
}

function generatePaymentPeriodArray(
  house,
  startDate,
  lastPayEndDate,
  noExistingPayment,
  managementFeeGstStatus
) {
  // Create a clone as we dont want to change the state
  const selectedHouse = JSON.parse(JSON.stringify(house))
  // console.log(selectedHouse)
  let dateArray = []
  let idStart =
    selectedHouse.street.substring(0, 2).toUpperCase() +
    selectedHouse.suburb.substring(0, 2).toUpperCase()

  // paymentFrequency is in weeks
  // Date first payment is made
  if (startDate === '' && selectedHouse.first_pay_time === null) {
    return dateArray
  }

  let latestPayDueDate = Moment(startDate || selectedHouse.first_pay_time)
  // Date for starting rent
  let latestPayFromDate = null
  if (noExistingPayment > 0) {
    latestPayFromDate = Moment(lastPayEndDate)
    latestPayFromDate.add(1, 'days')
  } else {
    latestPayFromDate = Moment(selectedHouse.checkin_time)
  }

  // Date for ending rent
  let endPayToDate = Moment(selectedHouse.checkout_time)
  // let currentDate = Moment()
  // Rent per week
  let rentalAmount = selectedHouse.rental_amount
  let rentalRate = selectedHouse.rental_rate / 100
  let payFrequency = selectedHouse.pay_frequency // Weeks
  let payFrequencyDays = payFrequency * 7

  if (payFrequencyDays <= 0) {
    return []
  }

  // Calculate if the days are within the week
  // Get remaining days
  let remainingDays =
    (endPayToDate.clone().diff(latestPayFromDate, 'days') + 1) %
    payFrequencyDays
  let totalPaymentCycles = Math.floor(
    (endPayToDate.clone().diff(latestPayFromDate, 'days') + 1) /
      payFrequencyDays
  )

  // Cacluate the total rent required for each frequency
  let periodPaymentTotal = rentalAmount * payFrequency //* (1 - rentalRate)
  let remainingPayment = (rentalAmount / 7) * remainingDays //* (1 - rentalRate)

  // Calculation starts from the next payment cycle, only for existing entries
  if (noExistingPayment > 0) {
    latestPayDueDate.add(payFrequencyDays, 'days')
  }
  const account = JSON.parse(window.sessionStorage.getItem('user'))

  for (let i = 0; i < totalPaymentCycles; i++) {
    let fee =
      i + 1 >= totalPaymentCycles
        ? periodPaymentTotal + remainingPayment
        : periodPaymentTotal
    let payEndDate =
      i + 1 >= totalPaymentCycles
        ? endPayToDate
        : latestPayFromDate
            .clone()
            .add(payFrequencyDays, 'days')
            .subtract(1, 'days')
    dateArray.push({
      id: '',
      payment_no: idStart + latestPayDueDate.format('YYYYMMDD'),
      house_id: selectedHouse.id,
      account_id: account.accountId,
      company_id: account.companyId,
      pay_frequency: selectedHouse.pay_frequency,
      pay_amount: Number(parseFloat(fee).toFixed(2)),
      manager_fee: Number(parseFloat(fee * rentalRate).toFixed(2)),
      manager_fee_real: '',
      mf_gst_status: '',
      manager_fee_gst: Number(
        parseFloat(
          managementFeeGstStatus > 0 ? fee * rentalRate * 0.15 : 0
        ).toFixed(2)
      ),
      total_amount: '',
      op_user: '',
      paydue_time: latestPayDueDate.format('YYYY-MM-DD'),
      paystart_time: latestPayFromDate.format('YYYY-MM-DD'),
      payend_time: payEndDate.format('YYYY-MM-DD'),
      state: '0',
      fee: [],
    })
    latestPayDueDate.add(payFrequencyDays, 'days')
    latestPayFromDate = payEndDate.clone().add(1, 'days')
  }
  return sortPayments(dateArray)
}

function sortPayments(payments) {
  return payments.sort((a, b) => Moment(a.paydue_time) - Moment(b.paydue_time))
}

function findHouseAddressById(houseId, houseAddressArray) {
  let houseObject
  for (houseObject of houseAddressArray) {
    if (houseObject.house_id === houseId) {
      return houseObject
    }
  }
}

export default storeModule
